import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, onBeforeUnmount, toRefs, computed } from 'vue';
import { useQuasar } from 'quasar';

// Stores
import { useUiStore } from '@stores/ui'
import { useSecurityStore } from '@stores/security';

// Personal Components
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QCustomInput from '@components/form/QCustomInput.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// Personal libs
import {
  displayDialogError,
  displayDialogSuccess,
} from '@shared/lib/notify.lib';
import { $t } from '@i18n/utils';
import authRepository from '@data/repository/auth.repository';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import { FormNameEnum } from '@shared/enum/form-name.enum';
import { InputFiedTypeEnum } from '@shared/enum/input.enum'

/**
 * Globals
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogForgottenPass',
  props: {
  show: { required: false, type: Boolean, default: false },
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {

// Vendor
const $q = useQuasar();

/**
 * Events
 */
const emits = __emit;

/**
 * Stores
 */
const uiStore = useUiStore()
const securityStore = useSecurityStore()

/**
 * HTMLElement ref
 */
const qInputEmail = ref<typeof QCustomInput>();

/**
 * Datas
 */
const email = ref<string | undefined>(undefined);
const loading = ref<boolean | undefined>(false);

/**
 * Computed properties
 */
const tempPassDuration = computed<number>(() => uiStore.config?.password?.tempPasswordDuration ?? 0)

/**
 * Properties
 */
const props = __props;
const { show } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function onBtnClose() {
  onClose();
}

function validForm(): boolean {
    // Reset focus
    qInputEmail.value && qInputEmail.value?.blur()

    // Validate
    qInputEmail.value && qInputEmail.value?.validate()

    if (qInputEmail.value != null && qInputEmail.value.hasError()) {
      qInputEmail.value.focus()
      return false;
    }

    return true;
}

async function onSubmit() {
  if (!validForm()) {
    return;
  }

  loading.value = true;

  authRepository
    .forgottenPass({
      email: email.value,
      csrf: (await securityStore.getCsrf(FormNameEnum.FORGOTTEN_PASSWORD))?.token
    })
    .then(() => {
      displayDialogSuccess({
        title: 'Mot de passe oublié',
        message: $t('forgottenPass.msg.success', { tempPassDuration: tempPassDuration.value })
      })

      // Close popup
      onClose();
    })
    .catch((error: Error) => {
      displayDialogError({
        title: 'Erreur',
        message: error.message ?? 'null',
        isHtml: true
      });
    })
    .finally(() => {
      loading.value = false;
      onClose();
    });
}

/**
 * ****************************** Hooks
 */
onBeforeUnmount(() => {
  email.value = undefined;
  loading.value = undefined;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    ref: "qDialogTemplate",
    "show-dialog": _unref(show),
    title: _unref($t)('forgottenPass.title'),
    maximized: false,
    "dialog-size": _unref(DialogSizeEnum).SMALL,
    onClose: _cache[2] || (_cache[2] = ($event: any) => (onClose()))
  }, {
    content: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius q-pt-sm"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)('forgottenPass.desc', { tempPassDuration: tempPassDuration.value })), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
            default: _withCtx(() => [
              _createVNode(QCustomInput, {
                modelValue: email.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
                ref_key: "qInputEmail",
                ref: qInputEmail,
                "input-type": _unref(InputFiedTypeEnum).EMAIL,
                disable: loading.value
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "disable"])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_separator),
          _createVNode(_component_q_card_actions, { align: "right" }, {
            default: _withCtx(() => [
              _createVNode(QBtnCancel, {
                label: _unref($t)('forgottenPass.btn.cancel.label'),
                disable: loading.value,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (onBtnClose()))
              }, null, 8 /* PROPS */, ["label", "disable"]),
              _createVNode(QBtnValid, {
                label: _unref($t)('forgottenPass.btn.ok.label'),
                "loading-label": _unref($t)('forgottenPass.btn.ok.loadingLabel'),
                disable: loading.value,
                loading: loading.value,
                "icon-right": 'send',
                onClick: onSubmit
              }, null, 8 /* PROPS */, ["label", "loading-label", "disable", "loading"])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "title", "dialog-size"]))
}
}

})